import { Recipe } from '../../lib/store';
import { formatMacronutrient } from '../../lib/utils';

// Function to calculate nutrition percentages based on user goals
export const calculateNutritionPercentages = (recipe: Recipe, userGoals?: {
  proteinTarget?: number;
  carbLimit?: number;
  fatRange?: { min: number; max: number };
}) => {
  if (!recipe.nutrition) {
    return {
      protein: { percentage: 0, value: 0, target: 0, status: 'unknown' },
      carbs: { percentage: 0, value: 0, target: 0, status: 'unknown' },
      fat: { percentage: 0, value: 0, target: 0, status: 'unknown' }
    };
  }

  const defaults = {
    proteinTarget: userGoals?.proteinTarget || 120, // Default protein goal
    carbLimit: userGoals?.carbLimit || 20, // Default carb limit (keto-style)
    fatRange: userGoals?.fatRange || { min: 50, max: 70 } // Default fat range
  };

  const nutrition = recipe.nutrition;
  
  // Safe number extraction with fallback to 0 for null/undefined/NaN values
  const safeProtein = Number(nutrition.protein) || 0;
  const safeCarbs = Number(nutrition.carbs) || 0;
  const safeFat = Number(nutrition.fat) || 0;
  
  // Validate that we have valid numbers before calculation
  const proteinPercentage = isNaN(safeProtein) || isNaN(defaults.proteinTarget) || defaults.proteinTarget === 0 
    ? 0 
    : Math.min((safeProtein / defaults.proteinTarget) * 100, 100);
    
  const carbsPercentage = isNaN(safeCarbs) || isNaN(defaults.carbLimit) || defaults.carbLimit === 0 
    ? 0 
    : Math.min((safeCarbs / defaults.carbLimit) * 100, 100);
    
  const fatPercentage = isNaN(safeFat) || isNaN(defaults.fatRange.max) || defaults.fatRange.max === 0 
    ? 0 
    : Math.min((safeFat / defaults.fatRange.max) * 100, 100);
  
  const protein = {
    percentage: proteinPercentage,
    value: formatMacronutrient(safeProtein),
    target: defaults.proteinTarget,
    status: safeProtein >= defaults.proteinTarget ? 'good' : 'low' as 'good' | 'low' | 'high'
  };

  const carbs = {
    percentage: carbsPercentage,
    value: formatMacronutrient(safeCarbs),
    target: defaults.carbLimit,
    status: (safeCarbs <= defaults.carbLimit * 0.75 ? 'good' : 
             safeCarbs <= defaults.carbLimit ? 'moderate' : 'high') as 'good' | 'moderate' | 'high'
  };

  const fat = {
    percentage: fatPercentage,
    value: formatMacronutrient(safeFat),
    target: defaults.fatRange.max,
    status: (safeFat >= defaults.fatRange.min && safeFat <= defaults.fatRange.max ? 'good' :
             safeFat < defaults.fatRange.min ? 'low' : 'high') as 'good' | 'low' | 'high'
  };

  return { protein, carbs, fat };
};

// Function to extract top 3 hero ingredients from recipe
export const getHeroIngredients = (recipe: Recipe): Array<{ name: string; emoji: string; category: string }> => {
  if (!recipe.ingredients || recipe.ingredients.length === 0) {
    return [];
  }

  // Category to emoji mapping for ingredients
  const categoryEmojiMap: Record<string, string> = {
    'meat': '🥩',
    'poultry': '🍗',
    'fish': '🐟',
    'seafood': '🦐',
    'dairy': '🧀',
    'vegetables': '🥬',
    'fruits': '🍎',
    'grains': '🍚',
    'legumes': '🫘',
    'nuts': '🥜',
    'spices': '🌶️',
    'herbs': '🌿',
    'condiments': '🧂',
    'oils': '🫒',
    'sweeteners': '🍯',
    'beverages': '🥤',
    'default': '🥘'
  };

  // Ingredient name to category and emoji mapping
  const ingredientMapping: Record<string, { emoji: string; category: string }> = {
    // Proteins
    'chicken': { emoji: '🍗', category: 'poultry' },
    'beef': { emoji: '🥩', category: 'meat' },
    'pork': { emoji: '🥓', category: 'meat' },
    'fish': { emoji: '🐟', category: 'fish' },
    'salmon': { emoji: '🐟', category: 'fish' },
    'tuna': { emoji: '🐟', category: 'fish' },
    'shrimp': { emoji: '🦐', category: 'seafood' },
    'tofu': { emoji: '🧈', category: 'legumes' },
    'eggs': { emoji: '🥚', category: 'dairy' },
    
    // Carbs
    'rice': { emoji: '🍚', category: 'grains' },
    'pasta': { emoji: '🍝', category: 'grains' },
    'bread': { emoji: '🍞', category: 'grains' },
    'quinoa': { emoji: '🌾', category: 'grains' },
    'potatoes': { emoji: '🥔', category: 'vegetables' },
    'sweet potato': { emoji: '🍠', category: 'vegetables' },
    
    // Vegetables
    'tomato': { emoji: '🍅', category: 'vegetables' },
    'onion': { emoji: '🧅', category: 'vegetables' },
    'garlic': { emoji: '🧄', category: 'vegetables' },
    'peppers': { emoji: '🌶️', category: 'vegetables' },
    'bell pepper': { emoji: '🫑', category: 'vegetables' },
    'mushrooms': { emoji: '🍄', category: 'vegetables' },
    'spinach': { emoji: '🥬', category: 'vegetables' },
    'lettuce': { emoji: '🥬', category: 'vegetables' },
    'cucumber': { emoji: '🥒', category: 'vegetables' },
    'carrots': { emoji: '🥕', category: 'vegetables' },
    'broccoli': { emoji: '🥦', category: 'vegetables' },
    
    // Common herbs and spices
    'basil': { emoji: '🌿', category: 'herbs' },
    'cilantro': { emoji: '🌿', category: 'herbs' },
    'parsley': { emoji: '🌿', category: 'herbs' },
    'oregano': { emoji: '🌿', category: 'herbs' },
    'thyme': { emoji: '🌿', category: 'herbs' },
    'rosemary': { emoji: '🌿', category: 'herbs' },
    
    // Dairy
    'cheese': { emoji: '🧀', category: 'dairy' },
    'milk': { emoji: '🥛', category: 'dairy' },
    'yogurt': { emoji: '🍪', category: 'dairy' },
    'butter': { emoji: '🧈', category: 'dairy' },
    
    // Oils and fats
    'olive oil': { emoji: '🫒', category: 'oils' },
    'coconut oil': { emoji: '🥥', category: 'oils' },
    'avocado': { emoji: '🥑', category: 'fruits' },
    
    // Common ingredients
    'lemon': { emoji: '🍋', category: 'fruits' },
    'lime': { emoji: '🍋', category: 'fruits' },
    'ginger': { emoji: '🫚', category: 'spices' },
    'soy sauce': { emoji: '🧂', category: 'condiments' },
    'salt': { emoji: '🧂', category: 'condiments' },
    'pepper': { emoji: '🌶️', category: 'spices' }
  };

  const heroIngredients: Array<{ name: string; emoji: string; category: string }> = [];
  
  // Process ingredients to find hero ingredients
  const processedIngredients = recipe.ingredients.slice(0, 6); // Look at first 6 ingredients
  
  for (const ingredient of processedIngredients) {
    if (heroIngredients.length >= 3) break;
    
    // Ensure ingredientName is always a string
    let ingredientName: string;
    if (typeof ingredient === 'string') {
      ingredientName = ingredient.toLowerCase();
    } else if (ingredient && typeof ingredient === 'object' && 'name' in ingredient) {
      // Handle ingredient objects with name property
      ingredientName = String((ingredient as any).name || '').toLowerCase();
    } else {
      // Fallback for any other type
      ingredientName = String(ingredient || '').toLowerCase();
    }
    
    // Skip empty ingredient names
    if (!ingredientName || ingredientName.trim() === '') {
      continue;
    }
    
    // Try to match ingredient to our mapping
    let matched = false;
    for (const [key, value] of Object.entries(ingredientMapping)) {
      if (ingredientName.includes(key)) {
        // Avoid duplicates of same category (except vegetables which can have multiple)
        const existingCategory = heroIngredients.find(h => h.category === value.category);
        if (!existingCategory || value.category === 'vegetables') {
          heroIngredients.push({
            name: key.charAt(0).toUpperCase() + key.slice(1),
            emoji: value.emoji,
            category: value.category
          });
          matched = true;
          break;
        }
      }
    }
    
    // If no match found, create a generic ingredient
    if (!matched && heroIngredients.length < 3) {
      const cleanName = ingredientName.split(' ')[0]; // Take first word
      heroIngredients.push({
        name: cleanName.charAt(0).toUpperCase() + cleanName.slice(1),
        emoji: categoryEmojiMap.default,
        category: 'other'
      });
    }
  }
  
  // If we still don't have 3, fill with default
  while (heroIngredients.length < 3) {
    heroIngredients.push({
      name: 'Ingredient',
      emoji: '🥘',
      category: 'other'
    });
  }
  
  return heroIngredients.slice(0, 3);
};

// Get appropriate color for nutrition status
export const getNutritionStatusColor = (
  macroType: 'protein' | 'carbs' | 'fat', 
  status: 'good' | 'low' | 'moderate' | 'high' | 'unknown'
): { bg: string; text: string; bar: string } => {
  const defaultColor = { bg: 'bg-gray-50 dark:bg-gray-900/30', text: 'text-gray-700 dark:text-gray-300', bar: 'bg-gray-500' };
  
  if (macroType === 'protein') {
    const proteinColors: Record<string, { bg: string; text: string; bar: string }> = {
      good: { bg: 'bg-blue-50 dark:bg-blue-900/30', text: 'text-blue-700 dark:text-blue-300', bar: 'bg-blue-500' },
      low: { bg: 'bg-amber-50 dark:bg-amber-900/30', text: 'text-amber-700 dark:text-amber-300', bar: 'bg-amber-500' },
      unknown: defaultColor
    };
    return proteinColors[status] || defaultColor;
  }
  
  if (macroType === 'carbs') {
    const carbColors: Record<string, { bg: string; text: string; bar: string }> = {
      good: { bg: 'bg-green-50 dark:bg-green-900/30', text: 'text-green-700 dark:text-green-300', bar: 'bg-green-500' },
      moderate: { bg: 'bg-amber-50 dark:bg-amber-900/30', text: 'text-amber-700 dark:text-amber-300', bar: 'bg-amber-500' },
      high: { bg: 'bg-red-50 dark:bg-red-900/30', text: 'text-red-700 dark:text-red-300', bar: 'bg-red-500' },
      unknown: defaultColor
    };
    return carbColors[status] || defaultColor;
  }
  
  if (macroType === 'fat') {
    const fatColors: Record<string, { bg: string; text: string; bar: string }> = {
      good: { bg: 'bg-orange-50 dark:bg-orange-900/30', text: 'text-orange-700 dark:text-orange-300', bar: 'bg-orange-500' },
      low: { bg: 'bg-amber-50 dark:bg-amber-900/30', text: 'text-amber-700 dark:text-amber-300', bar: 'bg-amber-500' },
      high: { bg: 'bg-red-50 dark:bg-red-900/30', text: 'text-red-700 dark:text-red-300', bar: 'bg-red-500' },
      unknown: defaultColor
    };
    return fatColors[status] || defaultColor;
  }
  
  return defaultColor;
}; 